import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { PmsUINzAntModule } from 'shared/ui';
import { en_US } from 'ng-zorro-antd/i18n';
import { DatePipe, registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PmsUIAppRoutingModule } from './PmsUIApp-routing.module';
import { IconsProviderModule } from './icons-provider.module';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { AlertMessageService } from 'shared/core';

// Core layout components. Sign-in, the tenant picker and sign-out are the
// library's — one page each, for every vertical.
import { IndexComponent } from './index/index.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { PaymentBlockedComponent } from './pages/payment-blocked/payment-blocked.component';

// Shared modules
import { SharedModule } from 'shared/ui';
// The always-mounted overlays this vertical contributes to the shared shell. They are rexine
// screens — a sale order's drawer, its proforma-invoice drawer, the stock-label scanner and the
// stock-label edit modal — so the shell does not name them; this application does.
import {
  AiChatWidgetComponent,
  BarcodeLabelUpdateModalComponent,
  BCScannerViewComponent,
  PIDrawerViewComponent,
  SoDrawerComponent,
} from 'shared/ui';
import { FormulationCodeInputModule } from './Shared/Components/FormulationCodeInput/formulation-code-input.module';
import { ConfirmationModalModule } from './Shared/Components/ConfirmationModal/confirmation-modal.module';

import { ActiveX, environment } from '../environments/environment';
import {
  ALL_MENU_STRUCTURES,
  KF_API_CONFIG,
  kfApiConfigFromEnvironment,
  withMenuContributions,
  withMenuGroupsAfter,
} from 'shared/core';
import { KF_APP_CONFIG, KF_MENU, KF_SHELL_WIDGETS, KfMenuSource, provideKanzenflowAuth } from 'shared';
import { TANNERY_DASHBOARD_MENU_GROUPS, TANNERY_MENU_CONTRIBUTIONS, TANNERY_MENU_GROUPS } from 'tannery/core';
import { of } from 'rxjs';

import { NzQRCodeModule } from 'ng-zorro-antd/qr-code';
import { DragDropModule } from '@angular/cdk/drag-drop';

// Services (root-level providers)
import { ApiHttpService } from 'shared/core';
import { LoadingService } from 'shared/core';
// RoundOff pipe moved to SharedModule (cross-module usage)
import { TimelineService } from 'shared/core';
import { SoActivityTimelineService } from 'shared/core';
import { SoDrawerService } from 'shared/core';
import { PIDrawerService } from 'shared/core';
import { EODrawerService } from './Services/EODrawerService';
import { LoggingService } from 'shared/core';
import { LoggingConfigService } from 'shared/core';

// Components that remain eagerly loaded (not part of any lazy-loaded feature module)
import { SupplierListComponent } from './SupplierManagement/Supplier/supplier-list/supplier-list.component';
import { CustomerListComponent } from './CustomerManagement/customer-list/customer-list.component';
import { ProductionDowntimeReasonComponent } from './ProductionMasters/production-downtime-reason/production-downtime-reason.component';
import { LowGradeReasonComponent } from './ProductionMasters/low-grade-reason/low-grade-reason.component';
import { ProductTransferComponent } from './Product/producttransfer/producttransfer.component';

// Notification components
import { NotificationComponent } from './Notification/ProductionStagesList/notification/notification.component';
import { NotificationTypeConfigurationComponent } from './Notification/NotificationTypeConfiguration/notification-type-configuration.component';
import { NotificationGroupComponent } from './Notification/NotificationGroup/notification-group/notification-group.component';
import { WhatsAppTemplateListComponent } from './Notification/whats-app-template-list/whats-app-template-list.component';
import { WhatsappConfigComponent } from './Notification/whatsapp-config/whatsapp-config.component';
import { EmailGroupMappingComponent } from './Notification/email-group-mapping/email-group-mapping.component';

// Dashboard (DashboardHub stays eagerly loaded, sub-dashboards are lazy-loaded)
import { DashboardHubComponent } from './Dashboard/DashboardHub/DashboardHub.component';

// IATF Document Management
import { IATFDocumentManagementComponent } from './Features/IATFDocumentManagement/IATFDocumentManagement.component';
import { DocumentService } from 'shared/core';

// Print components (root-level routes, outside home layout)
import { VesselTagPrintComponent } from './Master/vessel/vessel-tag-print.component';
import { GatepassprintComponent } from './GateManagement/GatePassPrint/gatepassprint.component';
import { MixingprintComponent } from './Production/mixingprint/mixingprint.component';
import { MixingTicketPrintComponent } from './Production/mixingticketprint/mixing-ticket-print.component';
import { MasterMixTicketPrintComponent } from './Production/mixingticketprint/master-mix-ticket-print/master-mix-ticket-print.component';
import { MixingChildTicketPrintComponent } from './Production/mixingticketprint/mixing-child-ticket-print/mixing-child-ticket-print.component';
import { ProcessprintComponent } from './Production/processprint/processprint.component';
import { InspectionPrintComponent } from './Production/WorkPlan/inspectionprint/inspectionprint.component';
import { JumboPrintComponent } from './Production/WorkPlan/jumboprint/jumboprint.component';
import { InspectionAllPrintComponent } from './Production/WorkPlan/inspectionallprint/inspectionallprint.component';
import { InspectionDetailsPrintComponent } from './Production/WorkPlan/InspectionDetailsPrint/InspectionDetailsPrint.component';
import { InspectionDetailsMixingPrintComponent } from './Production/WorkPlan/InspectionDetailsMixingPrint/InspectionDetailsMixingPrint.component';
import { FormulationPrintComponent } from './Production/FormulationCode/Print/formulationprint.component';
import { OutPassPrintComponent } from './OutPass/outpassPrint/outpassprint.component';
import { OutPassPackingListPrintComponent } from './OutPass/outpassPackingListPrint/outpasspackinglistprint.component';
import { proformaPrintComponent } from './SalesOrder/proformaInvoice/Print/proformaPrint.component';
// PackingListPrintComponent moved to SharedModule (cross-module usage)
import { PackagingListEmailComponent } from './Dispatch/Packaging/Email/PackagingListEmail.component';
import { CostingListPrintComponent } from './Costing/CostingListPrint/CostingListPrint.component';
import { CostEstimationListPrintComponent } from './Costing/Estimation/PrintList/costEstimationListPrint.component';
import { CostEstimationPrintComponent } from './Costing/Estimation/Print/costEstimationPrint.component';
import { SingleOrderCostingPrint } from './Costing/SingleOrderCostingPrint/SingleOrderCostingPrint.component';

// BarcodeLabelPrint moved to shared/ui with the rest of BarcodeLabelManagement: it is opened by
// the barcode modals there, by Inventory and by the pigment/MB screens, and both applications now
// route to it with loadComponent.
// BCScannerView, StockLabelTimeline, BulkStockInspection, BulkExistingStockLabelUpdate,
// BarcodeLabelUpdateModal moved to SharedModule (cross-module usage)

// PrdDowntime popup: rexine's own always-mounted widget, contributed to the shared shell.
import { PrdDowntimeComponent } from './Features/PrdDowntime/components/PrdDowntime/prddowntime.component';
import { TallySyncRunStatusComponent } from './Features/TallyIntegration/components/tally-sync-run-status/TallySyncRunStatus.component';
import { TallySyncHistoryComponent } from './Features/TallyIntegration/components/tally-sync-history/TallySyncHistory.component';

// DateRangePickerComponent now available through SharedModule

registerLocaleData(en);
declare var ClientID: any;

/**
 * One-time migration: clear stale MSAL v2/v3 cache keys.
 *
 * At module scope on purpose. It has to run before the PublicClientApplication
 * is constructed, and provideKanzenflowAuth constructs it from a factory the
 * injector calls the first time the instance token is asked for — which is after
 * every provider array has been evaluated. Importing this file is the last
 * moment that is reliably earlier. It is rexine's alone: no other vertical has
 * a v2/v3 cache to migrate, so it is not an option on the library's provider.
 */
function clearStaleMsalCacheOnce(): void {
  try {
    const MIGRATION_KEY = 'pms_msal_v5_migrated';
    if (localStorage.getItem(MIGRATION_KEY)) return; // Already migrated

    // Look for old MSAL v2/v3 keys that are incompatible with v5
    const oldKeys = Object.keys(localStorage).filter(key =>
      key.includes('login.windows.net') ||
      key.includes('login.microsoftonline.com')
    );

    if (oldKeys.length > 0) {
      console.warn(`One-time migration: clearing ${oldKeys.length} stale MSAL v2/v3 cache entries`);
      // Clear all msal-related keys to ensure clean slate for v5
      Object.keys(localStorage)
        .filter(key => key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))
        .forEach(key => localStorage.removeItem(key));
    }

    localStorage.setItem(MIGRATION_KEY, '1');
  } catch (e) {
    // localStorage may be unavailable — ignore
  }
}

clearStaleMsalCacheOnce();

@NgModule({ declarations: [
        // Core layout components
        IndexComponent,
        UnauthorizedComponent,
        PaymentBlockedComponent,

        // Eagerly-loaded page components (not in any feature module)
        SupplierListComponent,
        CustomerListComponent,
        ProductionDowntimeReasonComponent,
        LowGradeReasonComponent,
        ProductTransferComponent,

        // Notification components
        NotificationComponent,
        NotificationTypeConfigurationComponent,
        NotificationGroupComponent,
        WhatsAppTemplateListComponent,
        WhatsappConfigComponent,
        EmailGroupMappingComponent,

        // Dashboard
        DashboardHubComponent,

        // IATF Document Management
        IATFDocumentManagementComponent,

        // Print components (root-level routes, outside home layout)
        VesselTagPrintComponent,
        GatepassprintComponent,
        // RejectedStockSlipComponent is standalone in shared/ui: the print route loadComponents it and the Inventory feature imports it
        MixingprintComponent,
        MixingTicketPrintComponent,
        MasterMixTicketPrintComponent,
        MixingChildTicketPrintComponent,
        ProcessprintComponent,
        InspectionPrintComponent,
        JumboPrintComponent,
        InspectionAllPrintComponent,
        InspectionDetailsPrintComponent,
        InspectionDetailsMixingPrintComponent,
        FormulationPrintComponent,
        OutPassPrintComponent,
        OutPassPackingListPrintComponent,
        proformaPrintComponent,
        PackagingListEmailComponent,
        CostingListPrintComponent,
        CostEstimationListPrintComponent,
        CostEstimationPrintComponent,
        SingleOrderCostingPrint,

    ],
    bootstrap: [IndexComponent], imports: [BrowserModule,
        TallySyncRunStatusComponent,
        TallySyncHistoryComponent,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        PmsUIAppRoutingModule,
        IconsProviderModule,
        NzLayoutModule,
        NzMenuModule,
        PmsUINzAntModule,
        SharedModule,
        FormulationCodeInputModule,
        ConfirmationModalModule,
        NzQRCodeModule,
        DragDropModule,
        // Standalone, and mounted by the shared shell rather than declared into a template here.
        PrdDowntimeComponent
    ], providers: [
        // Where the APIs are, for code that has moved into the shared library and
        // can no longer import this app's environment file. Rexine keeps the file
        // and answers the question from it; tannery answers from its config.json.
        { provide: KF_API_CONFIG, useValue: kfApiConfigFromEnvironment(environment, ActiveX) },
        // What the shared shell and tenancy layer know about this application: its name, where the
        // Admin API is, its images, and where each unit type's tenants land. The name is the
        // product's: the sign-in page and the pre-tenant header show it before any company is
        // chosen, so a division name (which belongs to the tenant the picker selects) would be
        // wrong there.
        {
            provide: KF_APP_CONFIG,
            useValue: {
                appName: 'KanzenFlow',
                adminApiUrl: environment.Admin_Api_Url,
                // Where the picker lands: the welcome screen, for every unit type.
                // A vertical may register its own landing in homeRoutes by unit
                // type; the tannery registered its masters page until 2026-09-07,
                // when the user asked for the welcome page for all tenants.
                homeRoute: 'home/welcome',
                homeRoutes: {},
                logoUrl: 'assets/img/s-logo.png',
                brandImageUrl: 'assets/img/zaibunco.png',
                userAvatarUrl: 'assets/img/avatarlogo.png',
                signInLogoUrl: 'assets/img/kanzenflow-logo.png',
                signInBackgroundUrl: 'assets/img/14.png',
                changePasswordUrl: 'https://portal.microsoftonline.com/ChangePassword.aspx',
            },
        },
        // The navigation: every configured group, plus each mounted vertical's own top-level
        // families (TANNERY_MENU_GROUPS, spliced in after Reports; TANNERY_DASHBOARD_MENU_GROUPS,
        // spliced in right after the now rexine-scoped 'dashboard' group — Sprint 15a Phase 0,
        // the prototype puts its dashboard first) and its contributions into the families it
        // genuinely shares with rexine (TANNERY_MENU_CONTRIBUTIONS, today just Master — a
        // tannery tenant finds its leather masters there, in a 'Tannery Masters' sub-family with
        // Articles beside it). A vertical-owned family carries its own `unitType` and the shell
        // drops it for every other tenant (ADR-P-002 v2.0, amending D6); which items a tenant
        // sees inside any family is still decided by the operations its database holds (seeded
        // by unit-type scope from the admin catalogue) and the user's roles, so listing them all
        // here costs a rexine tenant nothing.
        {
            provide: KF_MENU,
            useValue: (() =>
                of(
                    withMenuContributions(
                        withMenuGroupsAfter(
                            withMenuGroupsAfter(ALL_MENU_STRUCTURES, 'dashboard', TANNERY_DASHBOARD_MENU_GROUPS),
                            'reports',
                            TANNERY_MENU_GROUPS
                        ),
                        TANNERY_MENU_CONTRIBUTIONS
                    )
                )) as KfMenuSource,
        },
        // Mounted once by the shell, for the whole session, because any screen can open them.
        // The order is the order the shell's own template used to mount them in, which is the
        // order they render in — it matters to nothing but a diff, and a diff is easier to read
        // when it does not reorder. Their services are root singletons, so exactly one of each
        // must exist: a second instance would answer show() too and open a duplicate.
        { provide: KF_SHELL_WIDGETS, useValue: SoDrawerComponent, multi: true },
        { provide: KF_SHELL_WIDGETS, useValue: PIDrawerViewComponent, multi: true },
        { provide: KF_SHELL_WIDGETS, useValue: BCScannerViewComponent, multi: true },
        { provide: KF_SHELL_WIDGETS, useValue: BarcodeLabelUpdateModalComponent, multi: true },
        { provide: KF_SHELL_WIDGETS, useValue: PrdDowntimeComponent, multi: true },
        // Sprint 16: reaches every tenant (tannery and Rexine both) — the widget
        // itself is vertical-agnostic; AiChatService routes to whichever
        // backend the current tenant's unit type owns.
        { provide: KF_SHELL_WIDGETS, useValue: AiChatWidgetComponent, multi: true },
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        { provide: NZ_I18N, useValue: en_US },
        AlertMessageService,
        LoadingService,
        TimelineService,
        SoActivityTimelineService,
        SoDrawerService,
        // Root-scoped for the same reason as SoDrawerService: the PI drawer is opened from
        // SharedModule's SO drawer as well as from the Sales Order screens. A single root instance
        // keeps both sides talking to the same service.
        PIDrawerService,
        EODrawerService,
        ApiHttpService,
        // BarcodeScannerService, BarcodeLabelUpdateService, StockLabelTimelineService moved to SharedModule
        LoggingService,
        LoggingConfigService,
        DocumentService,
        // BulkStockInspectionService, BulkExistingStockLabelUpdateService moved to SharedModule
        // The whole auth layer in one call: the MSAL instance and its services,
        // the guard configuration, HttpClient with KfInterceptor, and the
        // initializer that completes a redirect sign-in and records the session.
        // apiScope is derived — `<client-id>/.default`, which is the only
        // audience APIM's validate-jwt accepts — so no deployment can get it
        // wrong in a config file.
        provideKanzenflowAuth({
            clientId: ActiveX.clientId,
            authority: ActiveX.authority,
            redirectUri: ActiveX.redirectUri,
            postLogoutRedirectUri: ActiveX.postLogoutRedirectUri,
        }),
        DatePipe,
    ] })
export class PmsUIAppModule { }
